import { IBlock } from '../interfaces/IBlock';
import { IReviewNew } from '../interfaces/IReviewNew';

type Review = IReviewNew['review'];

export interface IDescriptorField {
  label: string;
  value: string;
}

export interface IPlatformDescriptor {
  reviewText: (review: Review) => string | null | undefined;
  author: (review: Review) => string | null | undefined;
  fields: (review: Review) => IDescriptorField[];
}

export interface IAiReviewDescriptor {
  prompt: string;
  author: string;
  hasReviewText: boolean;
}

// Scraped reviews store an absent field as '' about as often as null, so `??` is not enough here:
// it would keep the empty string and make the review look text-less to `hasReviewText`.
const firstNonEmpty = (...values: (string | null | undefined)[]): string | undefined =>
  values.find((value) => !!value && value.trim() !== '') ?? undefined;

const field = (label: string, value: unknown): IDescriptorField[] =>
  value === null || value === undefined || String(value).trim() === ''
    ? []
    : [{ label, value: String(value).trim() }];

const blockFields = (label: string, blocks?: IBlock[] | null): IDescriptorField[] => {
  if (!blocks || blocks.length === 0) return [];

  const lines = blocks
    .filter((block) => block.title || block.text || block.rating)
    .map((block) => {
      const rating =
        block.rating === null || block.rating === undefined || block.rating === ''
          ? ''
          : `${block.rating}/5`;
      return [block.title, rating, block.text].filter(Boolean).join(' — ');
    })
    .filter((line) => line !== '');

  return lines.length === 0 ? [] : [{ label, value: '\n  ' + lines.join('\n  ') }];
};

const assessmentFields = (
  label: string,
  assessments?: { title?: string | null; rating: string | number }[] | null
): IDescriptorField[] => {
  if (!assessments || assessments.length === 0) return [];

  const lines = assessments
    .map((assessment) => [assessment.title, `${assessment.rating}/5`].filter(Boolean).join(' — '))
    .filter((line) => line !== '');

  return lines.length === 0 ? [] : [{ label, value: '\n  ' + lines.join('\n  ') }];
};

const reviewTextAndName: IPlatformDescriptor = {
  reviewText: (review) => review.reviewText,
  author: (review) => review.name,
  fields: () => [],
};

const textAndUsername: IPlatformDescriptor = {
  reviewText: (review) => review.text,
  author: (review) => review.username,
  fields: () => [],
};

const genericDescriptor: IPlatformDescriptor = {
  reviewText: (review) => firstNonEmpty(review.text, review.reviewText),
  author: (review) => firstNonEmpty(review.username, review.name),
  fields: (review) => [
    ...field('Title', review.header),
    ...field('Title', review.reviewTitle),
    ...field('Title', review.title),
    ...field('Reviewer position', review.position),
    ...field('Reviewer position', review.positionText),
    ...field('Positive', review.positiveFeedback),
    ...field('Negative', review.negativeFeedback),
    ...blockFields('Sub-ratings', review.blocks),
  ],
};

const platformDescriptors: Record<string, IPlatformDescriptor> = {
  googleplace: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  facebook: { ...(reviewTextAndName as unknown as IPlatformDescriptor), fields: () => [] },
  tripadvisor: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  hotels: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  ebay: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  capterra: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  playstore: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  indeed: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    // `reviewerTitle` is the reviewer's job title, not the review's headline — Indeed renders the
    // two separately (IndeedReview.tsx), so they must not share the `Title` label.
    fields: (review) => [
      ...field('Title', review.reviewTitle),
      ...field('Reviewer position', review.reviewerTitle),
    ],
  },
  airbnb: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  amazonde: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  appstore: {
    ...(reviewTextAndName as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  google: {
    reviewText: (review) => review.comment,
    author: (review) => review.reviewer?.displayName,
    fields: () => [],
  },
  booking: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
      ...field('Stay date', review.stayDate),
      ...field('Stay duration', review.stayDuration),
      ...field('Room type', review.roomType),
      ...field('Traveler type', review.travelerType),
      ...field('Country', review.userCountry),
    ],
  },
  expedia: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
      ...field('Stay date', review.stayDate),
      ...field('Stay duration', review.travelDuration),
      ...field('Traveler type', review.traveledAs?.join(', ')),
    ],
  },
  holidaycheck: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.header),
  },
  trivago: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  fewo: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Title', review.reviewTitle),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
    ],
  },
  yelp: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Visited', review.dateVisited),
      ...field('Location', review.userLocation),
    ],
  },
  ekomi: { ...(textAndUsername as unknown as IPlatformDescriptor), fields: () => [] },
  check24: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.header),
  },
  golocal: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.header),
  },
  gutebanken: {
    reviewText: (review) => review.text,
    author: (review) => review.name,
    fields: (review) => field('Title', review.reviewTitle),
  },
  trustedshops: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => field('Title', review.reviewTitle),
  },
  trustpilot: {
    reviewText: (review) => firstNonEmpty(review.text, review.reviewText),
    author: (review) => review.username,
    fields: (review) => field('Title', review.title),
  },
  jobvoting: {
    reviewText: (review) => review.text,
    author: (review) => review.name,
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Title', review.reviewTitle),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  autoscout24: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Reviewer position', review.positionText),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  omr: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  mobile: {
    reviewText: () => undefined,
    author: (review) => review.username,
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Reviewer position', review.positionText),
      ...field('Positive', review.positiveFeedback),
      ...field('Negative', review.negativeFeedback),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  kununu: {
    reviewText: () => undefined,
    author: (review) => review.username,
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Reviewer position', review.position),
      ...field('Reviewer position', review.positionText),
      ...field('Recommendation', review.recommendation),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  glassdoor: {
    reviewText: () => undefined,
    author: (review) => review.username,
    fields: (review) => [
      ...field('Title', review.header),
      ...field('Reviewer position', review.position),
      ...field('Location', review.location),
      ...field('Employment status', review.employmentStatus),
      ...field('Pros', review.textCompanyPros),
      ...field('Cons', review.textCompanyCons),
      ...blockFields('Sub-ratings', review.blocks),
    ],
  },
  jameda: {
    ...(textAndUsername as unknown as IPlatformDescriptor),
    fields: (review) => [
      ...field('Title', review.title),
      ...field('Age', review.age),
      ...field('Insurance', review.insurance),
      ...field('Location', review.location),
      ...field('Treated by', review.medicalPractitioner),
      ...assessmentFields('Mandatory assessments', review.mandatoryAssessments),
      ...assessmentFields('Optional assessments', review.optionalAssessments),
    ],
  },
};

export const buildAiReviewDescriptor = (reviewEntry: IReviewNew): IAiReviewDescriptor => {
  const descriptor = platformDescriptors[reviewEntry.platform] ?? genericDescriptor;
  const review = reviewEntry.review;
  const currentReviewText = descriptor.reviewText(review);
  const hasReviewText = !!currentReviewText && currentReviewText.trim() !== '';
  const lines = [
    ...(hasReviewText ? [{ label: 'Review text', value: currentReviewText.trim() }] : []),
    ...descriptor.fields(review),
  ];

  return {
    prompt: lines.map(({ label, value }) => `${label}: ${value}`).join('\n'),
    author: (descriptor.author(review) ?? '').toString().trim(),
    hasReviewText,
  };
};
